import { useState, type FC } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

export interface ValuePropCardData {
    /** Card title (displayed in all states) */
    title: string;
    /** Card description (displayed when card is active/playing) */
    description?: string;
    /** Optional link text (displayed when card is active/playing) */
    linkText?: string;
    /** Optional link URL */
    linkHref?: string;
    /** Image source URL. If both imageSrc and videoSrc are provided, imageSrc takes priority */
    imageSrc?: string;
    /** Video source URL (e.g. mp4). Used when imageSrc is not provided */
    videoSrc?: string;
    /** Alt text for the media (used as alt for images, aria-label for videos) */
    mediaAlt?: string;
}

interface ValuePropCardProps {
    card: ValuePropCardData;
    isActive: boolean;
    isHovered: boolean;
    isAnyCardHovered: boolean;
    index: number;
    dataEventProps?: DataEventProps;
    onActivate?: () => void;
    onHoverStart?: () => void;
}

const containerCss = css({
    display: "flex",
    flexDirection: "row",
    cursor: "pointer",
    textAlign: "left",
    width: "100%"
});

const progressBarContainerCss = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "6px",
    flexShrink: 0,
    borderRadius: "3px",
    backgroundColor: wacToken("color.neutral.light300"),
    overflow: "hidden",
    position: "relative"
});

const progressBarFillCss = css({
    position: "absolute",
    insetBlockStart: 0,
    insetInlineStart: 0,
    width: "100%",
    backgroundColor: wacToken("color.primary.blue"),
    borderRadius: "3px",
    transition: "none"
});

const contentCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100"),
    paddingInlineStart: token("space.200"),
    flex: 1
});

const titleCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
    "margin": "0 !important",
    "&:hover, &:focus": {
        color: wacToken("color.primary.blue")
    }
});

// Hardcoded because wacToken does not resolve correctly at runtime
const inactiveTitleCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, local/ensure-wac-token-usage
    color: "#505258 !important"
});

const inactiveTitleHighlightedCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
    color: `${wacToken("color.primary.blue")} !important`
});

const descriptionCss = css({
    margin: 0
});

const cardButtonCss = css({
    "display": "block",
    "width": "100%",
    "border": "none",
    "background": "none",
    "padding": 0,
    "textAlign": "left",
    "cursor": "pointer",

    "&:focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});

const linkContainerCss = css({
    paddingBlockStart: token("space.050")
});

export const ValuePropCard: FC<ValuePropCardProps> = ({
    card,
    isActive,
    isHovered,
    isAnyCardHovered,
    index,
    dataEventProps,
    onActivate,
    onHoverStart
}) => {
    const [isFocused, setIsFocused] = useState(false);

    const showBar = (isActive && !isAnyCardHovered) || isHovered;

    return (
        <div
            css={containerCss}
            data-testid={`value-prop-card-${index}`}
            data-active={isActive}
            aria-current={isActive ? "true" : undefined}
            onMouseEnter={onHoverStart}
            onFocus={() => {
                setIsFocused(true);
                onHoverStart?.();
            }}
            onBlur={() => {
                setIsFocused(false);
            }}
        >
            <div
                css={progressBarContainerCss}
                style={{ visibility: showBar ? "visible" : "hidden" }}
            >
                <div css={progressBarFillCss} style={{ height: "100%" }} />
            </div>
            <div css={contentCss}>
                {!isActive ? (
                    <button
                        type="button"
                        css={cardButtonCss}
                        onClick={onActivate}
                    >
                        <p
                            className="card-title-sm"
                            css={[
                                titleCss,
                                inactiveTitleCss,
                                (isHovered || isFocused) &&
                                    inactiveTitleHighlightedCss
                            ]}
                        >
                            {card.title}
                        </p>
                    </button>
                ) : (
                    <>
                        <p className="card-title-sm" css={titleCss}>
                            {card.title}
                        </p>
                        {card.description && (
                            <p className="body-md" css={descriptionCss}>
                                {card.description}
                            </p>
                        )}
                        {card.linkText && card.linkHref && (
                            <div css={linkContainerCss}>
                                <Link
                                    aria-label={card.linkText}
                                    text={card.linkText}
                                    href={card.linkHref}
                                    type="primary"
                                    dataEventProps={dataEventProps}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
